import * as THREE from "three";
import {
  DEFAULT_CLASS_ID,
  PLAYER_EYE_HEIGHT,
  PLAYER_HEIGHT,
  aimDir,
  buildMapColliders, getClass, rayVsAabb, stepPlayer,
  type Aabb, type PlayerInput, type PlayerState, type Vec3,
} from "@mmo/shared";
import { buildMap } from "./map";
import { Net, defaultServerUrl } from "./net";
import { RemotePlayers } from "./remote-players";
import { Tracers } from "./tracers";
import { ViewModel } from "./view-model";
import { Character } from "./character";
import { lookupModel } from "./model-registry";

export type Game = {
  start: () => void;
  resize: () => void;
  setPointerLocked: (locked: boolean) => void;
  refreshClass: () => void;
};

type KeyInput = { forward: number; right: number; jump: boolean; sprint: boolean; melee: boolean; reload: boolean };
type FeedEntry = { text: string; addedAt: number };

export function createGame(
  canvas: HTMLCanvasElement,
  hud: HTMLDivElement,
  getName: () => string,
  getClassId: () => string,
): Game {
  const renderer = new THREE.WebGLRenderer({
    canvas,
    antialias: true,
    powerPreference: "high-performance",
    stencil: false,
  });
  renderer.setPixelRatio(Math.min(window.devicePixelRatio, 2));
  renderer.info.autoReset = false;

  const scene = new THREE.Scene();
  scene.background = new THREE.Color(0x87ceeb);
  scene.fog = new THREE.Fog(0x87ceeb, 60, 200);

  const camera = new THREE.PerspectiveCamera(75, 1, 0.1, 500);

  scene.add(new THREE.HemisphereLight(0xffffff, 0x444466, 0.9));
  const sun = new THREE.DirectionalLight(0xffffff, 0.7);
  sun.position.set(40, 60, 20);
  scene.add(sun);

  buildMap(scene);
  const colliders: Aabb[] = buildMapColliders();
  const remotes = new RemotePlayers(scene);
  const tracers = new Tracers(scene);
  const crosshair = document.getElementById("crosshair") as HTMLDivElement | null;

  // Camera must be in the scene tree for its children (view model) to render.
  scene.add(camera);
  const viewModel = new ViewModel(camera);
  let viewModelClassId = "";

  const killFeedEl = ensureKillFeed();
  const feed: FeedEntry[] = [];

  const initClass = getClass(getClassId());
  const self: PlayerState = {
    id: "",
    name: "me",
    classId: initClass.id,
    speedMultiplier: initClass.speedMultiplier,
    position: { x: 0, y: 0.5, z: 6 },
    velocity: { x: 0, y: 0, z: 0 },
    yaw: 0,
    pitch: 0,
    onGround: false,
    coyoteTimer: 0,
    jumpBufferTimer: 0,
    dodgingTimer: 0,
    dodgeCooldownTimer: 0,
    hp: initClass.hp,
    ammo: initClass.primary.magazineSize,
    reloadingUntil: 0,
    kills: 0,
    deaths: 0,
  };

  type ViewMode = "first" | "third";
  let viewMode: ViewMode = (localStorage.getItem("mmo:viewMode") as ViewMode) || "first";
  let localCharacter: Character | null = null;
  let localCharacterClassId = "";
  let dodgePending = false;

  let pointerLocked = false;
  let connected: "connecting" | "open" | "closed" = "connecting";
  let inputSeq = 0;
  const unacked: PlayerInput[] = [];
  let mouseDown = false;
  let rightMouseDown = false;
  let lastHitMarkerAt = 0;
  let lastLandHardAt = 0;
  let prevOnGround = false;
  let prevVy = 0;
  let bobPhase = 0;
  let currentFov = 75;
  const playerNames = new Map<string, string>();
  const BASE_FOV = 75;
  const MAX_FOV = 95;
  let nextLocalFireReadyAt = 0;
  let nextLocalMeleeReadyAt = 0;
  const aimVec = new THREE.Vector3();
  const ammoCountEl = document.getElementById("ammo-count") as HTMLDivElement | null;
  const reloadBarEl = document.getElementById("reload-bar") as HTMLDivElement | null;
  const reloadFillEl = reloadBarEl?.firstElementChild as HTMLDivElement | null;
  const cooldownRingEl = document.getElementById("cooldown-ring") as HTMLDivElement | null;
  const renderError = { x: 0, y: 0, z: 0 };
  const ERROR_SMOOTH_HALF_LIFE = 0.12;
  const ERROR_SNAP_THRESHOLD = 2.0;

  const keys = new Set<string>();
  window.addEventListener("keydown", (e) => {
    keys.add(e.code);
    if (e.code === "Space") e.preventDefault();
    if (e.code === "KeyQ" && pointerLocked) dodgePending = true;
    if (e.code === "KeyG" && pointerLocked) {
      viewMode = viewMode === "first" ? "third" : "first";
      localStorage.setItem("mmo:viewMode", viewMode);
    }
  });
  window.addEventListener("keyup", (e) => keys.delete(e.code));
  window.addEventListener("blur", () => { keys.clear(); mouseDown = false; rightMouseDown = false; });

  window.addEventListener("mousedown", (e) => {
    if (!pointerLocked) return;
    if (e.button === 0) mouseDown = true;
    if (e.button === 2) rightMouseDown = true;
  });
  window.addEventListener("mouseup", (e) => {
    if (e.button === 0) mouseDown = false;
    if (e.button === 2) rightMouseDown = false;
  });

  const SENSITIVITY = 0.0022;
  const PITCH_MAX = Math.PI / 2 - 0.01;

  function applyLook(dx: number, dy: number) {
    self.yaw -= dx * SENSITIVITY;
    self.pitch -= dy * SENSITIVITY;
    if (self.pitch > PITCH_MAX) self.pitch = PITCH_MAX;
    if (self.pitch < -PITCH_MAX) self.pitch = -PITCH_MAX;
  }

  window.addEventListener("pointermove", (e) => {
    if (!pointerLocked) return;
    // getCoalescedEvents exposes events the browser merged for rendering —
    // gives raw hardware-rate mouse deltas instead of one-per-frame throttling.
    const events = typeof e.getCoalescedEvents === "function" ? e.getCoalescedEvents() : [];
    if (events.length > 0) {
      for (const ce of events) applyLook(ce.movementX, ce.movementY);
    } else {
      applyLook(e.movementX, e.movementY);
    }
  });

  function readKeyInput(): KeyInput {
    let f = 0, r = 0;
    if (keys.has("KeyW")) f += 1;
    if (keys.has("KeyS")) f -= 1;
    if (keys.has("KeyD")) r += 1;
    if (keys.has("KeyA")) r -= 1;
    return {
      forward: f,
      right: r,
      jump: keys.has("Space"),
      sprint: keys.has("ShiftLeft") || keys.has("ShiftRight"),
      melee: keys.has("KeyV") || rightMouseDown,
      reload: keys.has("KeyR"),
    };
  }

  function nameFor(id: string) { return playerNames.get(id) ?? `player${id}`; }

  function pushFeed(text: string) {
    feed.push({ text, addedAt: performance.now() });
    if (feed.length > 5) feed.shift();
    renderFeed();
  }

  function renderFeed() {
    killFeedEl.innerHTML = feed.map((f) => `<div>${escapeHtml(f.text)}</div>`).join("");
  }

  const net = new Net(defaultServerUrl(), {
    onStatus: (s) => { connected = s; },
    onWelcome: (id) => {
      self.id = id;
      remotes.setLocalId(id);
      const name = getName();
      const classId = getClassId();
      self.name = name;
      self.classId = classId;
      self.speedMultiplier = getClass(classId).speedMultiplier;
      // Create the local third-person body once we have an id (for color hashing).
      if (!localCharacter) {
        localCharacter = new Character(scene, id, classId, lookupModel(name));
        localCharacterClassId = classId;
        localCharacter.group.visible = false;
      }
      net.send({ t: "hello", name, classId });
    },
    onSnapshot: (msg) => {
      for (const p of msg.players) playerNames.set(p.id, p.name);
      remotes.ingest(msg.serverTime, msg.players);
      if (!self.id) return;
      const serverSelf = msg.players.find((p) => p.id === self.id);
      const ackSeq = msg.lastAckSeq[self.id] ?? 0;
      if (!serverSelf) return;

      const prevX = self.position.x, prevY = self.position.y, prevZ = self.position.z;

      self.position.x = serverSelf.position.x;
      self.position.y = serverSelf.position.y;
      self.position.z = serverSelf.position.z;
      self.velocity.x = serverSelf.velocity.x;
      self.velocity.y = serverSelf.velocity.y;
      self.velocity.z = serverSelf.velocity.z;
      self.onGround = serverSelf.onGround;
      self.coyoteTimer = serverSelf.coyoteTimer;
      self.jumpBufferTimer = serverSelf.jumpBufferTimer;
      self.dodgingTimer = serverSelf.dodgingTimer;
      self.dodgeCooldownTimer = serverSelf.dodgeCooldownTimer;
      self.hp = serverSelf.hp;
      self.kills = serverSelf.kills;
      self.deaths = serverSelf.deaths;
      self.classId = serverSelf.classId;
      self.speedMultiplier = serverSelf.speedMultiplier;
      self.ammo = serverSelf.ammo;
      self.reloadingUntil = serverSelf.reloadingUntil;

      while (unacked.length && unacked[0].seq <= ackSeq) unacked.shift();
      for (const inp of unacked) stepPlayer(self, inp, colliders);

      // Smooth out the visible delta so the camera doesn't snap on every snapshot.
      // Large corrections (respawn, teleport) snap instead of slide.
      const dx = prevX - self.position.x + renderError.x;
      const dy = prevY - self.position.y + renderError.y;
      const dz = prevZ - self.position.z + renderError.z;
      const mag = Math.hypot(dx, dy, dz);
      if (mag > ERROR_SNAP_THRESHOLD) {
        renderError.x = 0; renderError.y = 0; renderError.z = 0;
      } else {
        renderError.x = dx; renderError.y = dy; renderError.z = dz;
      }
    },
    onShot: (msg) => {
      const isSelf = msg.shooterId === self.id;
      // Self-shots are predicted locally (view model, tracer, cooldown). The
      // server echo only carries the authoritative hit verdict.
      if (msg.kind === "primary" && !isSelf) {
        tracers.add(msg.origin, msg.end, msg.hitId !== null);
      }
      remotes.onShot(msg.shooterId, msg.kind);
      if (isSelf && msg.hitId !== null) {
        lastHitMarkerAt = performance.now();
      }
    },
    onHit: (_msg) => { /* hp comes via snapshot; nothing extra here */ },
    onDeath: (msg) => {
      const tag = msg.head ? " [HEAD]" : "";
      pushFeed(`${nameFor(msg.killerId)} killed ${nameFor(msg.playerId)}${tag}`);
    },
  });
  net.connect("player");

  function step(dt: number) {
    const k = readKeyInput();
    // Build input WITHOUT aim — we'll patch aim after the camera moves so the
    // server gets this frame's authoritative aim point in this frame's input.
    const input: PlayerInput = {
      seq: ++inputSeq,
      forward: k.forward,
      right: k.right,
      jump: k.jump,
      sprint: k.sprint,
      yaw: self.yaw,
      pitch: self.pitch,
      fire: mouseDown && self.hp > 0,
      melee: k.melee && self.hp > 0,
      reload: k.reload && self.hp > 0,
      dodge: dodgePending && self.hp > 0,
      aimX: 0, aimY: 0, aimZ: 0,
      dt,
    };
    dodgePending = false;
    if (self.id && self.hp > 0) stepPlayer(self, input, colliders);

    const hSpeed = Math.hypot(self.velocity.x, self.velocity.z);

    // Subtle camera bob — most of the running "feel" lives on the view model
    // gun sway instead so the head/camera doesn't bob nauseatingly.
    if (self.onGround && hSpeed > 0.5) {
      bobPhase += dt * (hSpeed * 1.5 + 2);
    } else {
      bobPhase += dt * 1.0;
    }
    const bobAmp = self.onGround ? Math.min(hSpeed / 7, 1) * 0.010 : 0;
    const bobY = Math.sin(bobPhase * 2) * bobAmp * 0.4;
    const bobX = Math.sin(bobPhase) * bobAmp * 0.5;

    // hard-landing dip
    if (self.onGround && !prevOnGround && prevVy < -10) {
      lastLandHardAt = performance.now();
    }
    const sinceLand = performance.now() - lastLandHardAt;
    const landDip = sinceLand < 220 ? Math.sin((sinceLand / 220) * Math.PI) * 0.08 : 0;
    prevOnGround = self.onGround;
    prevVy = self.velocity.y;

    // FOV: lerp toward target; sprint and over-max speed both expand it
    const speedExcess = Math.max(0, hSpeed - 7);
    const sprintBoost = k.sprint && hSpeed > 0.5 ? 5 : 0;
    const targetFov = Math.min(MAX_FOV, BASE_FOV + sprintBoost + speedExcess * 1.2);
    currentFov += (targetFov - currentFov) * Math.min(1, dt * 6);
    if (Math.abs(camera.fov - currentFov) > 0.05) {
      camera.fov = currentFov;
      camera.updateProjectionMatrix();
    }

    // exponential decay of reconciliation error so the camera slides smoothly
    const decay = Math.pow(0.5, dt / ERROR_SMOOTH_HALF_LIFE);
    renderError.x *= decay; renderError.y *= decay; renderError.z *= decay;

    const eyeX = self.position.x + renderError.x;
    const eyeY = self.position.y + renderError.y + PLAYER_EYE_HEIGHT;
    const eyeZ = self.position.z + renderError.z;

    camera.rotation.order = "YXZ";
    camera.rotation.y = self.yaw;
    camera.rotation.x = self.pitch;

    if (viewMode === "third") {
      // Over-the-shoulder offset (Arc-Raiders-style): up + right + back from the eye,
      // expressed in the camera-local frame. Raycast against map colliders so the
      // camera tucks in close to a wall instead of clipping through it.
      const sinY = Math.sin(self.yaw), cosY = Math.cos(self.yaw);
      const sinP = Math.sin(self.pitch), cosP = Math.cos(self.pitch);
      const fwdX = -sinY * cosP, fwdY = sinP, fwdZ = -cosY * cosP;
      const rightX = cosY, rightY = 0, rightZ = -sinY;
      const upX = -sinY * -sinP, upY = cosP, upZ = -cosY * -sinP;

      const RIGHT_OFFSET = 0.55, UP_OFFSET = 0.35, BACK_OFFSET = 3.0;
      const origin: Vec3 = { x: eyeX, y: eyeY, z: eyeZ };
      const targetX = eyeX - fwdX * BACK_OFFSET + rightX * RIGHT_OFFSET + upX * UP_OFFSET;
      const targetY = eyeY - fwdY * BACK_OFFSET + rightY * RIGHT_OFFSET + upY * UP_OFFSET;
      const targetZ = eyeZ - fwdZ * BACK_OFFSET + rightZ * RIGHT_OFFSET + upZ * UP_OFFSET;
      const dx = targetX - eyeX, dy = targetY - eyeY, dz = targetZ - eyeZ;
      const dist = Math.hypot(dx, dy, dz);
      const dir: Vec3 = { x: dx / dist, y: dy / dist, z: dz / dist };

      let camDist = dist;
      for (const c of colliders) {
        const t = rayVsAabb(origin, dir, c, camDist);
        if (t !== null) camDist = Math.max(0.3, t - 0.2);
      }
      camera.position.set(eyeX + dir.x * camDist, eyeY + dir.y * camDist, eyeZ + dir.z * camDist);
    } else {
      camera.position.set(eyeX + bobX, eyeY + bobY - landDip, eyeZ);
    }

    // ---- Aim point. In first-person the camera is at the eye, so a static
    // screen-center crosshair maps to (eye + lookDir * far). In third-person
    // the camera is offset, so we raycast from the CAMERA through screen
    // center to find what the crosshair is actually over; the server then
    // fires from the eye toward that point.
    const lookDir = aimDir(self.yaw, self.pitch);
    const cls = getClass(self.classId);
    const FAR_AIM = 200;
    let aimX: number, aimY: number, aimZ: number;
    if (viewMode === "third") {
      const cx = camera.position.x, cy = camera.position.y, cz = camera.position.z;
      const camOrigin: Vec3 = { x: cx, y: cy, z: cz };
      let t = FAR_AIM;
      for (const c of colliders) {
        const tt = rayVsAabb(camOrigin, lookDir, c, t);
        if (tt !== null && tt < t) t = tt;
      }
      aimX = cx + lookDir.x * t;
      aimY = cy + lookDir.y * t;
      aimZ = cz + lookDir.z * t;
    } else {
      aimX = eyeX + lookDir.x * FAR_AIM;
      aimY = eyeY + lookDir.y * FAR_AIM;
      aimZ = eyeZ + lookDir.z * FAR_AIM;
    }

    // ---- Predicted fire. Direction = eye → aim point. Tracer ends at the
    // first wall along that ray, capped by weapon range — exactly matches
    // what the server will compute server-side from the same aim point.
    const nowPredict = performance.now();
    if (self.id && mouseDown && self.hp > 0 && self.ammo > 0 && self.reloadingUntil === 0
        && nowPredict >= nextLocalFireReadyAt) {
      viewModel.triggerFire();
      localCharacter?.triggerFire();
      const fdx = aimX - eyeX, fdy = aimY - eyeY, fdz = aimZ - eyeZ;
      const fl = Math.hypot(fdx, fdy, fdz);
      const fireDir = fl > 0.5 ? { x: fdx / fl, y: fdy / fl, z: fdz / fl } : lookDir;
      let tracerT = cls.primary.range;
      const fireOrigin: Vec3 = { x: eyeX, y: eyeY, z: eyeZ };
      for (const c of colliders) {
        const t = rayVsAabb(fireOrigin, fireDir, c, tracerT);
        if (t !== null && t < tracerT) tracerT = t;
      }
      tracers.add(
        fireOrigin,
        { x: eyeX + fireDir.x * tracerT, y: eyeY + fireDir.y * tracerT, z: eyeZ + fireDir.z * tracerT },
        false,
      );
      nextLocalFireReadyAt = nowPredict + cls.primary.cooldownMs;
    }

    if (self.id && k.melee && self.hp > 0 && nowPredict >= nextLocalMeleeReadyAt) {
      viewModel.triggerMelee();
      localCharacter?.triggerMelee();
      nextLocalMeleeReadyAt = nowPredict + cls.melee.cooldownMs;
    }

    // Patch the aim point into this frame's input, then queue + send.
    input.aimX = aimX;
    input.aimY = aimY;
    input.aimZ = aimZ;
    if (self.id) {
      unacked.push(input);
      if (unacked.length > 200) unacked.splice(0, unacked.length - 200);
      net.send({ t: "input", input });
    }

    remotes.render(dt);
    tracers.update(performance.now());

    if (self.classId !== viewModelClassId) {
      viewModel.setClass(self.classId);
      viewModelClassId = self.classId;
    }
    viewModel.update(dt, hSpeed, self.onGround);
    viewModel.setVisible(viewMode === "first");

    if (localCharacter) {
      if (viewMode === "third" && self.id) {
        if (self.classId !== localCharacterClassId) {
          localCharacter.setClassId(self.classId);
          localCharacterClassId = self.classId;
        }
        localCharacter.setPose(self.position, self.yaw, self.velocity.x, self.velocity.z, self.onGround, self.dodgingTimer);
        localCharacter.update(dt);
        localCharacter.group.visible = true;
      } else {
        localCharacter.group.visible = false;
      }
    }

    updateWeaponHud();

    if (crosshair) {
      const since = performance.now() - lastHitMarkerAt;
      crosshair.style.filter = since < 120 ? "drop-shadow(0 0 4px #ff5555)" : "none";
    }
  }

  let lastTime = performance.now();
  let fpsAcc = 0, fpsFrames = 0, fps = 0;
  let hudFrame = 0;

  function loop() {
    const now = performance.now();
    let dt = (now - lastTime) / 1000;
    lastTime = now;
    if (dt > 0.1) dt = 0.1;

    step(dt);

    fpsAcc += dt; fpsFrames++;
    if (fpsAcc >= 0.5) {
      fps = Math.round(fpsFrames / fpsAcc);
      fpsAcc = 0; fpsFrames = 0;
    }
    if ((hudFrame++ & 3) === 0) {
      const cls = getClass(self.classId);
      const info = renderer.info;
      hud.textContent =
        `${connected}  ${cls.name}  id ${self.id || "-"}  fps ${fps}  ` +
        `hp ${self.hp}  k/d ${self.kills}/${self.deaths}  ` +
        `calls ${info.render.calls}  tris ${info.render.triangles}`;
      info.reset();
    }

    renderer.render(scene, camera);
    requestAnimationFrame(loop);
  }

  function updateWeaponHud() {
    if (!ammoCountEl) return;
    const cls = getClass(self.classId);
    const mag = cls.primary.magazineSize;
    const reloadMs = cls.primary.reloadMs;
    const cooldownMs = cls.primary.cooldownMs;
    const now = performance.now();

    // Reload bar: server sends reloadingUntil in Date.now scale; convert via delta to now.
    const reloadRemaining = self.reloadingUntil > 0 ? self.reloadingUntil - Date.now() : 0;
    if (reloadRemaining > 0) {
      const progress = 1 - reloadRemaining / reloadMs;
      reloadBarEl?.classList.add("active");
      if (reloadFillEl) reloadFillEl.style.width = `${Math.max(0, Math.min(1, progress)) * 100}%`;
      ammoCountEl.classList.add("reloading");
      ammoCountEl.classList.remove("low");
      ammoCountEl.textContent = `-- / ${mag}`;
    } else {
      reloadBarEl?.classList.remove("active");
      ammoCountEl.classList.remove("reloading");
      ammoCountEl.classList.toggle("low", self.ammo <= Math.max(1, Math.floor(mag * 0.25)));
      ammoCountEl.textContent = `${self.ammo} / ${mag}`;
    }

    // Cooldown ring around the crosshair — predicted locally from confirmed shots.
    if (cooldownRingEl) {
      const remaining = nextLocalFireReadyAt - now;
      if (remaining > 0) {
        cooldownRingEl.classList.add("active");
        cooldownRingEl.style.setProperty("--p", (remaining / cooldownMs).toFixed(3));
      } else {
        cooldownRingEl.classList.remove("active");
      }
    }
  }

  return {
    start: () => requestAnimationFrame(loop),
    resize: () => {
      const w = window.innerWidth, h = window.innerHeight;
      renderer.setSize(w, h, false);
      camera.aspect = w / h;
      camera.updateProjectionMatrix();
    },
    setPointerLocked: (v) => {
      pointerLocked = v;
      if (!v) { mouseDown = false; rightMouseDown = false; }
    },
    refreshClass: () => {
      const id = getClassId();
      if (id !== self.classId && self.id) {
        net.send({ t: "hello", name: getName(), classId: id });
      }
    },
  };
}

function ensureKillFeed(): HTMLDivElement {
  let el = document.getElementById("killfeed") as HTMLDivElement | null;
  if (el) return el;
  el = document.createElement("div");
  el.id = "killfeed";
  Object.assign(el.style, {
    position: "fixed",
    top: "8px",
    right: "8px",
    fontSize: "13px",
    fontFamily: 'ui-monospace, "Cascadia Code", Menlo, Consolas, monospace',
    background: "rgba(0,0,0,0.4)",
    padding: "6px 10px",
    borderRadius: "4px",
    pointerEvents: "none",
    zIndex: "5",
    textAlign: "right",
    maxWidth: "300px",
  } as CSSStyleDeclaration);
  document.body.appendChild(el);
  return el;
}

function escapeHtml(s: string): string {
  return s.replace(/[&<>"']/g, (c) =>
    c === "&" ? "&amp;" : c === "<" ? "&lt;" : c === ">" ? "&gt;" : c === '"' ? "&quot;" : "&#39;");
}
